import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, EllipsisVerticalIcon } from 'lucide-react';
import CandlesIcon from '@/shared/assets/candles-icon.svg?react';
import ArrowsDownUpIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemWithSubmenu,
  DropdownMenuTrigger,
} from '@/shared/components';
import { ActionMenuItem, ActionMenuItemContent } from '@/widgets/account-card';
import { cn } from '@/shared/utils';
import {
  ACCOUNT_TYPE_ICONS,
  ACCOUNT_TYPE_LABEL_KEYS,
} from '../config/accountTypeMeta';
import { TradingAccountEntryLabel } from './TradingAccountEntryLabel';
import type {
  TradingAssetAccountEntry,
  TradingAssetActionsVisibility,
  TradingAssetRow,
} from '../model';

const TRIGGER_CLASSNAME =
  'cursor-pointer p-0 relative shrink-0 w-[3.2rem] h-[3.2rem] rounded-[1rem] border border-stroke bg-background dark:bg-background hover:bg-secondary-bg dark:hover:bg-secondary-bg transition-colors flex items-center justify-center text-primary';

const getAccountEntryLabel = (
  account: TradingAssetAccountEntry,
  typeLabel: string
) => `${typeLabel} · ${account.accName ?? account.accNum}`;

type ComponentIcon = typeof CandlesIcon;

type Props = {
  row: TradingAssetRow;
  visibility: TradingAssetActionsVisibility;
  contentClassName?: string;
  triggerClassName?: string;
  submenuClassName?: string;
  submenuAside?: boolean;
  assetEntryLabels?: boolean;
  onTrade: (row: TradingAssetRow, account?: TradingAssetAccountEntry) => void;
  onTransfer: (
    row: TradingAssetRow,
    account?: TradingAssetAccountEntry
  ) => void;
};

const TradingAssetActionMenu = ({
  row,
  visibility,
  contentClassName,
  triggerClassName,
  submenuClassName,
  submenuAside = false,
  assetEntryLabels = false,
  onTrade,
  onTransfer,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const closeAnd = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  const renderAction = (
    key: string,
    icon: ComponentIcon,
    label: string,
    onAction: (row: TradingAssetRow, account?: TradingAssetAccountEntry) => void
  ) => {
    if (row.accounts.length <= 1) {
      return (
        <ActionMenuItem
          key={key}
          icon={icon}
          label={label}
          onClick={closeAnd(() => onAction(row))}
        />
      );
    }

    return (
      <DropdownMenuItemWithSubmenu
        key={key}
        className="h-[4.8rem] w-full rounded-[1.2rem] cursor-pointer pr-[0.5rem]"
        onClick={e => e.stopPropagation()}
        renderTrigger={() => (
          <ActionMenuItemContent icon={icon} label={label}>
            <div className="flex items-center justify-center w-[2rem] h-[2rem]">
              <ChevronRightIcon className="size-full text-tertiary stroke-[3]" />
            </div>
          </ActionMenuItemContent>
        )}
        sideOffset={submenuAside ? undefined : -230}
        alignOffset={submenuAside ? undefined : 56}
        collisionPadding={submenuAside ? undefined : 16}
        subContentClassName={cn('z-2 relative', contentClassName)}>
        <div
          className={cn(
            'bg-popover min-w-[22rem] max-w-[calc(100vw-4.8rem)] max-h-(--radix-dropdown-menu-content-available-height) overflow-y-auto rounded-[1.6rem] p-[0.8rem] flex flex-col *:shrink-0 border dark:border-transparent shadow-md',
            submenuClassName
          )}>
          {row.accounts.map(account =>
            assetEntryLabels ? (
              <DropdownMenuItem
                key={account.accNum}
                showOptionCheckmark={false}
                className="h-[4.8rem] w-full px-[1.2rem] rounded-[1.2rem] cursor-pointer hover:bg-secondary-bg focus:bg-secondary-bg"
                onClick={event => {
                  event.stopPropagation();
                  closeAnd(() => onAction(row, account))();
                }}>
                <TradingAccountEntryLabel row={row} account={account} />
              </DropdownMenuItem>
            ) : (
              <ActionMenuItem
                key={account.accNum}
                icon={ACCOUNT_TYPE_ICONS[account.accountType]}
                label={getAccountEntryLabel(
                  account,
                  t(ACCOUNT_TYPE_LABEL_KEYS[account.accountType])
                )}
                childrenClassName="px-[1.2rem]"
                onClick={closeAnd(() => onAction(row, account))}
              />
            )
          )}
        </div>
      </DropdownMenuItemWithSubmenu>
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        withChevron={false}
        aria-label={t('personalAccount.wallets.columns.actions')}
        className={cn(
          TRIGGER_CLASSNAME,
          open && 'bg-secondary-bg',
          triggerClassName
        )}>
        <EllipsisVerticalIcon className="size-[1.6rem]" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={cn(
          'w-[23.9rem] min-w-[23.9rem] p-0 rounded-[1.6rem]',
          contentClassName
        )}
        classNameViewport="w-full max-w-full p-[0.8rem]">
        {visibility.trade &&
          renderAction(
            'trade',
            CandlesIcon,
            t('personalAccount.overview.actions.trade'),
            onTrade
          )}
        {visibility.transfer &&
          renderAction(
            'transfer',
            ArrowsDownUpIcon,
            t('personalAccount.overview.actions.transfer'),
            onTransfer
          )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { TradingAssetActionMenu };
